import {
  CmmcLevel,
  CompanyCreateDto,
  CompanyRevenueDto,
  CompanyRevenueDtoPeriodEnum,
  CompanyType,
  CompanyUrlsDto,
  ResearchOrganizationContactDto,
  ResearchOrganizationDataDto,
  ResearchOrganizationFundingDto,
  ResearchOrganizationFundingType,
  ResearchOrganizationType,
} from '@@types/api.types';
import {
  LONG_DESCRIPTION_MAX_LENGTH,
  LONG_DESCRIPTION_VALIDATION_MESSAGES,
  NAME_MAX_LENGTH,
  NAME_MIN_LENGTH,
  NAME_VALIDATION_MESSAGES,
} from '@azymmetric/shared/validation/validation.constants';
import { zCoercedOptional, zDecimalNumber, zFilteredStringArray } from '@utils/zodv4.util';
import { z } from 'zod/v4';

const companyUrlsSchema = z
  .object({
    crunchbase: zCoercedOptional(z.url({ message: 'Please enter a valid Crunchbase address' }).optional()),
    linkedin: zCoercedOptional(z.url({ message: 'Please enter a valid LinkedIn address' }).optional()),
    twitter: zCoercedOptional(z.url({ message: 'Please enter a valid X (Twitter) address' }).optional()),
    website: zCoercedOptional(z.url({ message: 'Please enter a valid website address' }).optional()),
  })
  .strip() satisfies z.ZodType<CompanyUrlsDto>;

const companyRevenueSchema = z
  .object({
    amount: zDecimalNumber,
    period: zCoercedOptional(z.enum(CompanyRevenueDtoPeriodEnum).optional()),
    valuation: zDecimalNumber,
  })
  .strip() satisfies z.ZodType<CompanyRevenueDto>;

const researchOrganizationContactSchema = z
  .object({
    email: zCoercedOptional(z.email({ message: 'Enter a valid email address' }).optional()),
    name: zCoercedOptional(z.string().optional()),
  })
  .strip() satisfies z.ZodType<ResearchOrganizationContactDto>;

const researchOrganizationFundingSchema = z
  .object({
    countries: zFilteredStringArray(z.array(z.string().min(1, { message: 'Country is required' }))).optional(),
    type: zCoercedOptional(z.enum(ResearchOrganizationFundingType).optional()),
  })
  .strip() satisfies z.ZodType<ResearchOrganizationFundingDto>;

const researchOrganizationDataSchema = z
  .object({
    departmentHeadContact: zCoercedOptional(researchOrganizationContactSchema.optional()),
    departmentName: zCoercedOptional(z.string().optional()),
    foreignAffiliates: zFilteredStringArray(
      z.array(z.string().min(1, { message: 'Affiliate is required' })),
    ).optional(),
    funding: zCoercedOptional(researchOrganizationFundingSchema.optional()),
    ipLicense: zCoercedOptional(z.boolean().optional()),
    techTransferContact: zCoercedOptional(researchOrganizationContactSchema.optional()),
    type: zCoercedOptional(z.enum(ResearchOrganizationType).optional()),
  })
  .strip()
  .check((ctx) => {
    const data = ctx.value;
    if (
      data.funding &&
      data.funding?.type === ResearchOrganizationFundingType.FOREIGN_NATION &&
      (!data.funding.countries || data.funding.countries.length === 0)
    ) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'Funding countries are required when funding type is Foreign Nation',
        path: ['funding', 'countries'],
      });
    }

    if (
      data.funding &&
      data.funding?.countries &&
      data.funding.countries.length > 0 &&
      data.funding.type !== ResearchOrganizationFundingType.FOREIGN_NATION
    ) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'Funding countries can only be provided when funding type is Foreign Nation',
        path: ['funding', 'countries'],
      });
    }
  }) satisfies z.ZodType<ResearchOrganizationDataDto>;

export const companyCreateSchema = z
  .object({
    city: zCoercedOptional(z.string().optional()),
    cmmcLevel: zCoercedOptional(z.enum(CmmcLevel).optional()),
    country: zCoercedOptional(z.string().optional()),
    description: zCoercedOptional(
      z.string().max(LONG_DESCRIPTION_MAX_LENGTH, { message: LONG_DESCRIPTION_VALIDATION_MESSAGES.max }).optional(),
    ),
    name: z
      .string({ message: NAME_VALIDATION_MESSAGES.required })
      .trim()
      .min(NAME_MIN_LENGTH, { message: NAME_VALIDATION_MESSAGES.min })
      .max(NAME_MAX_LENGTH, { message: NAME_VALIDATION_MESSAGES.max }),
    previousGovernmentWorkCountries: zFilteredStringArray(
      z.array(z.string().min(1, { message: 'Country is required' })),
    ).optional(),
    previousGovernmentWorkServiceIds: zFilteredStringArray(
      z.array(z.string().min(1, { message: 'Service is required' })),
    ).optional(),
    researchOrganizationData: zCoercedOptional(researchOrganizationDataSchema.optional()),
    revenue: zCoercedOptional(companyRevenueSchema.optional()),
    samsUeiNumber: zCoercedOptional(
      z
        .string()
        .regex(/^[A-Za-z0-9]*$/, { message: 'SAMS UEI number must contain only letters and numbers' })
        .optional(),
    ),
    state: zCoercedOptional(z.string().optional()),
    type: z.enum([CompanyType.COMMERCIAL, CompanyType.RESEARCH, CompanyType.RESEARCH_PROJECT], {
      message: 'Please select an organization type',
    }),
    urls: zCoercedOptional(companyUrlsSchema.optional()),
    yearFounded: zCoercedOptional(
      z.coerce
        .number()
        .min(1800, { message: 'Year must be after 1800' })
        .max(new Date().getFullYear(), { message: 'Year cannot be in the future' })
        .optional(),
    ),
  })
  .strip() satisfies z.ZodType<CompanyCreateDto>;

type ResearchValidationInput = {
  hasForeignAffiliation?: boolean;
  hasFunding?: boolean;
  researchOrganizationData?: ResearchOrganizationDataDto | undefined;
  type: CompanyType;
};

const isResearchType = (type: CompanyType) => type === CompanyType.RESEARCH || type === CompanyType.RESEARCH_PROJECT;

const researchRequirementsCheck = (ctx: { value: ResearchValidationInput; issues: Array<any> }) => {
  const data = ctx.value;

  if (!isResearchType(data.type)) {
    return;
  }

  if (data.hasFunding) {
    const funding = data.researchOrganizationData?.funding;

    if (!funding || !funding.type) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'Select a funding type.',
        path: ['researchOrganizationData', 'funding', 'type'],
      });
    }

    if (funding?.type === ResearchOrganizationFundingType.FOREIGN_NATION) {
      const countries = funding.countries?.filter((country) => country && country.trim().length > 0) ?? [];
      if (countries.length === 0) {
        ctx.issues.push({
          code: 'custom',
          input: ctx.value,
          message: 'Select at least one funding country.',
          path: ['researchOrganizationData', 'funding', 'countries'],
        });
      }
    }
  }

  if (data.hasForeignAffiliation) {
    const affiliates =
      data.researchOrganizationData?.foreignAffiliates?.filter(
        (affiliate) => typeof affiliate === 'string' && affiliate.trim().length > 0,
      ) ?? [];

    if (affiliates.length === 0) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'Add at least one foreign affiliate.',
        path: ['researchOrganizationData', 'foreignAffiliates[0]'],
      });
    }
  }
};

export const companyFormSchemaBase = companyCreateSchema.extend({
  hasForeignAffiliation: z.boolean().default(false),
  hasFunding: z.boolean().default(false),
});

export const companyFormDataSchema = companyFormSchemaBase.extend({
  type: z.enum(CompanyType),
});

export const companyFormSchema = companyFormSchemaBase.check(researchRequirementsCheck);

export const typeStepSchema = companyFormSchemaBase.pick({
  type: true,
});

export const detailsCommercialSchema = companyFormSchemaBase.pick({
  city: true,
  cmmcLevel: true,
  country: true,
  description: true,
  name: true,
  previousGovernmentWorkCountries: true,
  previousGovernmentWorkServiceIds: true,
  samsUeiNumber: true,
  state: true,
  yearFounded: true,
});

export const detailsResearchSchema = companyFormSchemaBase
  .pick({
    city: true,
    country: true,
    description: true,
    hasForeignAffiliation: true,
    hasFunding: true,
    name: true,
    previousGovernmentWorkCountries: true,
    previousGovernmentWorkServiceIds: true,
    researchOrganizationData: true,
    state: true,
    type: true,
  })
  .check(researchRequirementsCheck);

export const financialStepSchema = companyFormSchemaBase.pick({
  revenue: true,
});

export const profileStepSchema = companyFormSchemaBase.pick({
  urls: true,
});

// AZ-451 D9: admin-created Intelligence orgs collect only the org-general Details fields (Name +
// Description + Location) — no commercial/research-specific fields and no Profile step. This schema
// validates both the Details step and the full admin create payload. `type` is forced to INTELLIGENCE
// at submit (the admin Type step is presentational), so it is intentionally NOT part of this pick,
// which keeps the innovator schemas (whose `type` excludes INTELLIGENCE) untouched.
export const detailsIntelSchema = companyFormSchemaBase.pick({
  city: true,
  country: true,
  description: true,
  name: true,
  state: true,
});

export type CompanyFormData = z.infer<typeof companyFormDataSchema>;
